import { FunctionComponent, useEffect, useRef, useState } from "react";
import { Card, Dialog } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useLazyQuery, useMutation } from "@apollo/client";
import "./SubjectsPopUp.scss";
import ToasterWithMessage from "../../toaster";
import axios from "axios";
import moment from "moment";
import { debounce } from "lodash";
import { ALL_SUBJECTS, GET_PRESIGNED_URL, INSERT_SUBJECTS } from ".././api/FileUploadQuery";
import images from "../../../assets/images";
import { CLASSROOM_S3_PATH, messages, patterns } from "../../../utils/constants";
import { CardCover } from "@mui/joy";
import AutoComplete from '../../AutoComplete/AutoComplete';
import { getScreenDimensions } from "../../../utils/helper";

export type FileUploadType = {
  open: boolean;
  subject: { id: number, subjectName: string|null; subjectImageUrl: string|null } | null;
  type: string;
  handleClosePopUp: ()=>void;
  onSaveSubject: (id: number, subjectName: string, subjectImageUrl: string )=>void;
  onDeleteSubject: (id: number, subjectName: string|null, subjectImageUrl: string|null )=>void;
};

interface Subject {
  sjName: string;
  sjOriginalName: string;
  sjId: string;
}

interface AllSubjectsData {
  allSubjects: {
    nodes: Subject[];
    totalCount: number;
  };
}

interface AllSubjectsVariables {
  sjName: string;
}

export interface InsertSubjectsVariables {
  sjName: string;
}

export interface InsertSubjectsResponse {
  insertSubjects: string;
}

interface SubjectOptionType {
  id?: string;
  inputValue?: string;
  title: string;
}

const { error: errorToast } = ToasterWithMessage();

const SubjectsPopUp: FunctionComponent<FileUploadType> = ({subject,open,type, handleClosePopUp, onSaveSubject,onDeleteSubject}) => {
  const { closeIcon, cloudGif } = images;
  const [fileName, setFileName] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<string>();
  const [filePreview, setFilePreview] = useState<string>();
  const { error } = ToasterWithMessage();
  const [fileNameError, setFileNameError] = useState<string | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [accepted, setAccepted] = useState<object>({});
  const bucketname = process.env.REACT_APP_MEDIA_BUCKET_NAME;
  const region = process.env.REACT_APP_REGION;
  const [url, setUrl] = useState<string>("");
  const baseUrl = process.env.REACT_APP_FRONTEND_URL;
  const [loading,setLoading] = useState<boolean>(false);
  const [selectedSubject, setSelectedSubject] = useState<SubjectOptionType | null>(null);
  const [subjects, setSubjects] = useState<SubjectOptionType[]>([]);
  const [isNewSubject, setIsNewSubject] = useState(false);
  const { isMobileScreen } = getScreenDimensions();

  const handleSubjectChange = (newValue: SubjectOptionType | null, isNew?: boolean) => {
    setFileNameError("");
    const subject = newValue?.inputValue ? { title: newValue.inputValue } : newValue;
    setSelectedSubject(subject);
    setFileName(subject?.title ?? "");

    setIsNewSubject(isNew ? true : false);

    if (hasSubmitted) {
      setFileNameError(validateFileName(subject?.title?.trim() ?? ""));
    }
  };

  const [getAllSubjects, { loading: allSubjectsLoading }] = useLazyQuery<AllSubjectsData, AllSubjectsVariables>(
    ALL_SUBJECTS,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const seenTitles = new Set<string>();

        setSubjects(
          data?.allSubjects?.nodes
            ?.map((subject) => subject.sjOriginalName ? { title: subject.sjOriginalName } : null)
            ?.filter(Boolean)
            ?.filter((item) => {
              if (seenTitles.has(item!.title)) {
                return false;
              }
              seenTitles.add(item!.title);
              return true;
            }) as { title: string }[]
        );
      },
      onError: (error) => {
        errorToast(error.message);
      },
    }
  );

  const [insertSubject] = useMutation<InsertSubjectsResponse, InsertSubjectsVariables>(INSERT_SUBJECTS, {
    onCompleted: (data) => {
      console.log("Insert subject result:", data);
    },
    onError: (error) => {
      console.error("Insert subject error:", error);
      // errorToast(error.message);
    },
  });

  const debouncedSearch = useRef(
    debounce((searchTerm: string) => {
      getAllSubjects({
        variables: { sjName: `${searchTerm}%` },
      });
    }, 500)
  ).current;

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, []);

  useEffect(()=>{
    setUrl('');
    setFilePreview('');
    setFile(null);
    setFileName('');
    setFileNameError('');
    setLoading(false);
     if(type === "editSubject" && subject){
      if(subject?.subjectImageUrl){
        setFilePreview(baseUrl+subject?.subjectImageUrl);
        setUrl(subject?.subjectImageUrl);
      }
      if(subject?.subjectName){
        setFileName(subject?.subjectName);
        setSelectedSubject({ title: subject?.subjectName });
      }
     }
  },[type]);

  // Drag and drop the image
  const { getRootProps, getInputProps } = useDropzone({
    accept: { ...accepted },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles?.length !== 0) {
        const maxAllowedSize = 5 * 1024 * 1024; // 5MB

        if (acceptedFiles[0].size <= maxAllowedSize) {
          setFile(acceptedFiles[0]);
          const selectedFileType = acceptedFiles[0]?.type.split("/")?.[0];
          setFileType(selectedFileType);
          setFilePreview(URL.createObjectURL(acceptedFiles[0]));
        }
        else {
            error("Image size must be less than or equal to 5 MB");
        }
      }
    },
    onDropRejected(fileRejections, event) {
        if (fileRejections?.[0]?.errors?.[0]?.code === 'file-invalid-type') {
          error(`Please select either jpg, jpeg, png or svg image`);
        }
    },
  });

  const [getPreSignedUrl] = useLazyQuery(GET_PRESIGNED_URL, {
    fetchPolicy: "network-only",
    onCompleted: (data) =>{},
    onError: (err) => {}
  });

  useEffect(() => {
    setAcceptedFileFormats();
  }, []);

  const setAcceptedFileFormats = () => {
    setAccepted({
      'image/jpeg': [],
      'image/png': [],
      'image/svg+xml': [],
      'image/jpg': []
    });
  };

  const validateFileName = (value: string) => {
    if (!value) return "Subject is required";
    if (!patterns.fileNameLengthRegex.test(value)) return messages.subjectLength;
    // if (!patterns.alphabetSpace.test(value)) return messages.validSubject;
    return null;
  };

  const handleClose = () => {
    setFile(null);
    setFilePreview("");
    setFileName("");
    setHasSubmitted(false);
    setFileNameError(null);
    handleClosePopUp();
    setLoading(false);
    setSelectedSubject(null);
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);
    const validationError = validateFileName(fileName?.trim());
    if (validationError) {
      setFileNameError(validationError);
      return;
    }
    try {
      if (isNewSubject) {
        await insertSubject({ variables: { sjName: fileName } });
      }

      if(file){
      setLoading(true);
      const sanitizedFileName = fileName.replace(/ /g, '_')?.trim();
      const extension = file?.name.split('.').pop() || '';
      const newFileName = `${CLASSROOM_S3_PATH}images/${sanitizedFileName}-${moment().unix()}.${extension}`;
      const { data } = await getPreSignedUrl({ variables: { input: { key: newFileName } } });
      const { preSignedUrl } = data.getUploadPresignedUrl;

      await axios.put(preSignedUrl, file, {
        headers: { "Content-Type": file?.type },
        onUploadProgress: (progressEvent) => handleUploadProgress(progressEvent)
      }).then((data) => {
      });
      const fileUrl = preSignedUrl.split('?')[0];
      const uploadurl = fileUrl.split(`https://${bucketname}.s3.${region}.amazonaws.com/`)[1];
      setUrl(uploadurl);
      if(type === 'addSubject' && subject && uploadurl){
        onSaveSubject(
            subject?.id,
            fileName?.trim(),
            uploadurl
        );
        handleClose();
      }else if(type === 'editSubject' && subject && uploadurl){
        onSaveSubject(
          subject?.id,
          fileName?.trim(),
          uploadurl
      );
      handleClose();
      }
      }else{
        if(type === 'addSubject' && subject){
          handleClose();
          onSaveSubject(
              subject?.id,
              fileName?.trim(),
              url
          );
        }else if(type === 'editSubject' && subject){
          handleClose();
          onSaveSubject(
            subject?.id,
            fileName?.trim(),
            url
        );
        }
      }
    } catch (err) {
      error("Failed to upload the image");
    }
  };

  const handleUploadProgress = (progressEvent: any) => {
    if (progressEvent?.loaded && progressEvent?.total) {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    } else {
      console.log('Invalid progress event:', progressEvent);
    }
  };

  const handleDisplayText = () => "Image";

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFileName(value?.trim());
    setFileNameError('');
    if(hasSubmitted){
      const validationError = validateFileName(value?.trim());
      setFileNameError(validationError);
    }
  };

    //To remove the last value in input
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && fileName?.length === 1) {
      setFileName('');
      setFileNameError("Subject is required");
    }
  };

  // Add an event listener for the Enter key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault(); // Prevent the default form submission
        handleSubmit();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [fileName, file]); // Ensure the latest fileName and file are used

  return (
    <>
      {open && <Dialog
        open={open}
        PaperProps={{
          style: {
            borderRadius: "20px",
            width: `${isMobileScreen ? "300px" : "580px"}`,
            minHeight:'312px'
          },
        }}
      >
        <div className="uploadSubjectPopupMain">
          <div className="uploadSubjectPopup" style={{gap : '25px'}}>
            <div className="subjectPaddiv">
              <div className="uploadSubjectContainer">
                <label className="iconSubjectGif" {...getRootProps({ onClick: (e) => e.stopPropagation() })} style={{ cursor: 'default' }}>
                  {!file && !filePreview ? (
                    <img className="cloud1Icon" src={cloudGif} alt="cloud Gif"/>
                  ) : (
                    <Card
                      sx={{
                        ...(getScreenDimensions()?.isMobileScreen ? { height: "55px", padding: '0px' } : { padding: "10px", minHeight: "85px" }),
                        width: "100px",
                        margin: "5px",
                        padding: "10px"
                      }}
                    >
                      <CardCover sx={{
                        position:"relative"
                      }}>
                        {fileType === "video" ? (
                          <video autoPlay loop muted={fileType === "video"}  key={filePreview}>
                            <source src={filePreview} />
                          </video>
                        ) : (
                          <img className="previewSubjectImage" src={filePreview} alt="preview Icon"/>
                        )}
                      </CardCover>
                    </Card>
                  )}
                </label>
                <div className="contentSubjectCta">
                  <label className="dragDropSubjectContainer" {...getRootProps({ onClick: (e) => e.stopPropagation() })} style={{ cursor: 'default' }}>
                    <span className="dragDropSubjectContainer1">
                      <span>{`Drag & drop `}</span>
                      <span className="videos">
                        {handleDisplayText()}
                      </span>
                    </span>
                  </label>
                  <label className="orBrowseFilesSubjectContainer">
                    <span>{`or `}</span>
                    <span className="browseFiles" {...getRootProps()}>
                      browse files
                    </span>
                    <span> on your computer</span>
                  </label>
                  <input data-testid="imageUpload" {...getInputProps()} />
                </div>
              </div>
              <div className="fieldsMainSubjectContainer">
                  <div 
                      className="inputFieldsSubjectsContainer" 
                      style={{
                        width: '100%',
                        gap: '9px'
                      }}
                  >
                      <div 
                        className="fileSubjectUploading"
                      >
                        <div className="label">
                          <label className="subject">Subject</label>
                        </div>
                        <div className="inputSubjectContainer">
                          <AutoComplete
                            value={selectedSubject}
                            options={subjects}
                            loading={allSubjectsLoading}
                            onSearch={debouncedSearch}
                            placeholder={"Example: English"}
                            onChange={handleSubjectChange}
                            validate={validateFileName}
                            setError={setFileNameError}
                            onKeyDown={handleKeyDown}
                          />
                          {hasSubmitted === true && fileNameError && <div className="errorMessage">{fileNameError}</div>}
                        </div>
                      </div>
                  </div>
              </div>
            </div>
            <div className="ctaSubjectSet">
              { type === 'editSubject' && 
              <button className="deleteCta" disabled={loading} onClick={() => {subject && onDeleteSubject(subject?.id,subject?.subjectName,subject?.subjectImageUrl)}}>
                <label className="delete">Delete subject</label>
              </button>
              }
              <button className="uploadCta" disabled={loading} onClick={handleSubmit}>
                <label className="upload">Upload</label>
              </button>
            </div>
            <button className="icSubjectCancel" data-testid="closeButton" onClick={()=>{
              handleClose();
              }}>
              <img className="unionIconSubject" alt="closeIcon" src={closeIcon} />
            </button>
          </div>
        </div>
      </Dialog>}
    </>
  );
};

export default SubjectsPopUp;
